import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Award, ExternalLink, Globe } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    imagen_url: '',
    enlace: '',
    activo: true,
    orden: 0
  });

  const getWebsiteScreenshot = (url) => {
    if (!url) return null;
    try {
      const encodedUrl = encodeURIComponent(url);
      return `https://api.screenshotmachine.com?key=c04d3a&url=${encodedUrl}&dimension=1024x768&format=png&cacheLimit=0`;
    } catch {
      return null;
    }
  };

  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API}/sponsors`);
      setSponsors(response.data);
    } catch (error) {
      console.error('Error al cargar sponsors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSponsor) {
        await axios.put(`${API}/sponsors/${editingSponsor.id}`, formData);
      } else {
        await axios.post(`${API}/sponsors`, formData);
      }
      fetchSponsors();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      alert('Error al guardar sponsor');
    }
  };

  const deleteSponsor = (id, nombre) => {
    const confirmado = window.confirm(`¿Eliminar el sponsor "${nombre}"?`);
    if (confirmado) {
      axios.delete(`${API}/sponsors/${id}`)
        .then(() => {
          alert('Sponsor eliminado');
          fetchSponsors();
        })
        .catch((err) => {
          console.error(err);
          alert('Error al eliminar');
        });
    }
  };

  const editSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      nombre: sponsor.nombre,
      descripcion: sponsor.descripcion,
      imagen_url: sponsor.imagen_url || '',
      enlace: sponsor.enlace,
      activo: sponsor.activo,
      orden: sponsor.orden
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      imagen_url: '',
      enlace: '',
      activo: true,
      orden: 0
    });
    setEditingSponsor(null);
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="sponsors-title">
              Nuestros Sponsors
            </h1>
            <p className="text-gray-400">Empresas que apoyan a Mallorca Klan</p>
          </div>
          {user?.role === 'entrenador' && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary-mk" data-testid="btn-add-sponsor">
                  <Plus className="mr-2 h-4 w-4" /> Agregar Sponsor
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700">
                <DialogHeader>
                  <DialogTitle className="text-gold">
                    {editingSponsor ? 'Editar Sponsor' : 'Nuevo Sponsor'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre del Sponsor</Label>
                    <Input
                      id="nombre"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción</Label>
                    <Textarea
                      id="descripcion"
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="enlace">Sitio Web del Sponsor *</Label>
                    <Input
                      id="enlace"
                      value={formData.enlace}
                      onChange={(e) => setFormData({ ...formData, enlace: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="https://sponsor.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="imagen_url">Imagen Personalizada (opcional)</Label>
                    <Input
                      id="imagen_url"
                      value={formData.imagen_url}
                      onChange={(e) => setFormData({ ...formData, imagen_url: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="https://ejemplo.com/imagen.png"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="orden">Orden</Label>
                    <Input
                      id="orden"
                      type="number"
                      value={formData.orden}
                      onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) || 0 })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="activo"
                      checked={formData.activo}
                      onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
                    />
                    <Label htmlFor="activo">Sponsor Activo</Label>
                  </div>
                  <Button type="submit" className="w-full btn-primary-mk">
                    {editingSponsor ? 'Actualizar' : 'Crear'} Sponsor
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {sponsors.filter(s => s.activo || user?.role === 'entrenador').map((sponsor) => (
              <Card key={sponsor.id} className="card-mk overflow-hidden">
                {/* Imagen clickeable para ir al sitio */}
                <a 
                  href={sponsor.enlace} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block relative group"
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden relative">
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Globe className="w-16 h-16 text-gold mb-3 opacity-50" />
                      <span className="text-gray-400 text-lg font-medium">{getDomain(sponsor.enlace)}</span>
                    </div>
                    <img
                      src={sponsor.imagen_url || getWebsiteScreenshot(sponsor.enlace)}
                      alt={sponsor.nombre}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="flex items-center gap-2 text-white bg-gold px-4 py-2 rounded-lg">
                      <ExternalLink className="w-5 h-5" />
                      <span>Visitar Sitio</span>
                    </div>
                  </div>
                  {!sponsor.activo && (
                    <span className="absolute top-3 right-3 text-xs px-2 py-1 rounded bg-red-900 text-red-300">
                      Inactivo
                    </span>
                  )}
                </a>
                
                <CardContent className="p-5 space-y-4">
                  <CardTitle className="text-white text-xl">{sponsor.nombre}</CardTitle>
                  <p className="text-gray-300 text-sm">{sponsor.descripcion}</p>
                  
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Globe className="w-4 h-4" />
                    <span>{getDomain(sponsor.enlace)}</span>
                  </div>
                  
                  {user?.role === 'entrenador' && (
                    <div className="flex gap-2 pt-3 border-t border-gray-700">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                        onClick={() => editSponsor(sponsor)}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white border-red-600"
                        onClick={() => deleteSponsor(sponsor.id, sponsor.nombre)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {sponsors.filter(s => s.activo).length === 0 && user?.role !== 'entrenador' && (
              <div className="col-span-full">
                <Card className="card-mk border-gray-800">
                  <CardContent className="p-12 text-center">
                    <Award className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400">No hay sponsors activos aún</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {sponsors.length === 0 && user?.role === 'entrenador' && (
              <div className="col-span-full">
                <Card className="card-mk border-gray-800">
                  <CardContent className="p-12 text-center">
                    <Award className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400">No hay sponsors aún</p>
                    <p className="text-gray-500 text-sm mt-2">Agrega empresas que apoyen a Mallorca Klan</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Sponsors;
